import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import clsx from 'clsx';
import { sendNotification } from '@srm/ui/lib/notifications';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import { ChatAIHistoryService } from '@/services/ChatAIHistoryService';
import { chatAIHub } from '@/signalr/hubs/ChatAIHub';
import {
    listLoadStart,
    listLoadSuccess,
    listLoadError,
    setActiveConversation,
    startNewConversation,
    messagesLoadStart,
    messagesLoadSuccess,
    messagesLoadError,
    renameLocally,
    removeLocally,
    toggleSidebarMobile,
} from '@/store/reducers/ChatAIHistoryReducer';
import { ConversationList } from './ConversationList';
import { NewChatButton } from './NewChatButton';
import { RetentionNotice } from './RetentionNotice';
import { MdClose } from 'react-icons/md';
import './ChatAISidebar.scss';

const PAGE_SIZE = 30;
const REFETCH_DEBOUNCE_MS = 300;

/** Без пропсов: memo отрезает перерисовки окна чата (статус, ввод, ответы) — список подписан на стор сам. */
export const ChatAISidebar = React.memo(function ChatAISidebar() {
    const dispatch = useAppDispatch();
    // items/total читаем только в ref (через эффект ниже): подписка через useTypedSelector
    // нам тут не нужна — list рендерится в дочернем <ConversationList>, который сам подписан.
    // Главный плюс: handleCommitRename/performDelete/handleSelect не пересоздаются при каждом
    // SignalR-refetch и не каскадируют re-render всех ConversationListItem (visible flicker).
    const items = useTypedSelector((s) => s.chatAIHistory.items);
    const total = useTypedSelector((s) => s.chatAIHistory.listTotal);
    const sidebarOpenMobile = useTypedSelector((s) => s.chatAIHistory.sidebarOpenMobile);
    const activeId = useTypedSelector((s) => s.chatAIHistory.currentConversationId);

    const [renamingId, setRenamingId] = useState<string | null>(null);
    const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inFlightRef = useRef(false);
    /** Generation для отмены устаревшего select при быстрых кликах (H4). */
    const selectGenRef = useRef(0);
    /** Stable ref на последнюю loadList — чтобы SignalR-callback не захватил stale closure. */
    const loadListRef = useRef<(reset: boolean) => Promise<void>>(async () => {});
    /** Snapshot предыдущего total при оптимистичном delete — нужно для корректного rollback (M1). */
    const itemsRef = useRef(items);
    const totalRef = useRef(total);
    /** Active id — нужен SignalR-callback'у для silent refresh без stale closure. */
    const activeIdRef = useRef<string | null>(activeId);
    useEffect(() => {
        itemsRef.current = items;
        totalRef.current = total;
        activeIdRef.current = activeId;
    }, [items, total, activeId]);

    const loadList = useCallback(
        async (reset: boolean) => {
            if (inFlightRef.current && !reset) return;
            inFlightRef.current = true;
            const skip = reset ? 0 : itemsRef.current.length;
            dispatch(listLoadStart({ reset }));
            try {
                const { data } = await ChatAIHistoryService.listConversations({ skip, take: PAGE_SIZE });
                dispatch(listLoadSuccess({ items: data.items, total: data.total, reset }));
            } catch (e) {
                dispatch(listLoadError((e as Error).message));
            } finally {
                inFlightRef.current = false;
            }
        },
        [dispatch],
    );

    useEffect(() => {
        loadListRef.current = loadList;
    }, [loadList]);

    // Initial load + reload при SignalR push (debounced).
    // На push'е также silent-обновляем активный thread: сценарий tab A открыл тред,
    // tab B отправил сообщение — этот tab НЕ в group requestId (он не отправлял), поэтому
    // получает только conversationsUpdated. Без silent refresh открытый thread остался бы
    // на pending-плашке до ручного переключения.
    useEffect(() => {
        loadListRef.current(true);
        const unsub = chatAIHub.onConversationsUpdated(() => {
            if (refetchTimer.current) clearTimeout(refetchTimer.current);
            refetchTimer.current = setTimeout(() => {
                loadListRef.current(true);
                const aid = activeIdRef.current;
                if (aid) {
                    // Силент: без messagesLoadStart — не дёргаем loading-флаг, чтобы форма
                    // не показывала skeleton на каждом push'е. ChatAIForm useEffect увидит
                    // обновлённый messagesByConversation и переустановит form.messages.
                    ChatAIHistoryService.getConversation(aid)
                        .then(({ data }) => dispatch(messagesLoadSuccess({
                            conversationId: aid,
                            messages: data.messages,
                        })))
                        .catch(() => {
                            // 404: тред soft-deleted в другом табе — sidebar refetch выше
                            // удалит его из items, и активный сбросится в null через
                            // listLoadSuccess.
                        });
                }
            }, REFETCH_DEBOUNCE_MS);
        });
        return () => {
            unsub();
            if (refetchTimer.current) clearTimeout(refetchTimer.current);
        };
    }, []);

    // Fallback: если первый SignalR push потерян (race с OnConnectedAsync —
    // клиент ещё не успел вступить в group chatHistory:{userId}), всё равно
    // делаем refetch когда тред помечен как persisted (получили requestId от бэка).
    const isCurrentPersisted = useTypedSelector((s) => s.chatAIHistory.isCurrentPersisted);
    useEffect(() => {
        if (isCurrentPersisted) {
            if (refetchTimer.current) clearTimeout(refetchTimer.current);
            refetchTimer.current = setTimeout(() => loadListRef.current(true), REFETCH_DEBOUNCE_MS);
        }
    }, [isCurrentPersisted]);

    const handleSelect = useCallback(
        async (conversationId: string) => {
            // Защита от race: если тред в момент клика уже не в items (был удалён),
            // не активируем его — иначе после delete активный re-устанавливается на удалённый.
            if (!itemsRef.current.some((i) => i.conversationId === conversationId)) {
                return;
            }
            const myGen = ++selectGenRef.current;
            dispatch(setActiveConversation(conversationId));
            dispatch(toggleSidebarMobile(false));
            dispatch(messagesLoadStart(conversationId));
            try {
                const { data } = await ChatAIHistoryService.getConversation(conversationId);
                if (myGen !== selectGenRef.current) return; // юзер уже переключился — игнорируем устаревший ответ
                dispatch(messagesLoadSuccess({ conversationId, messages: data.messages }));
            } catch (e) {
                if (myGen !== selectGenRef.current) return;
                dispatch(messagesLoadError(conversationId));
                sendNotification({
                    id: `chat-ai-load-${conversationId}`,
                    title: 'Не удалось загрузить чат',
                    message: (e as Error).message,
                    variant: 'red',
                });
            }
        },
        [dispatch],
    );

    const handleNewChat = useCallback(() => {
        dispatch(startNewConversation());
        dispatch(toggleSidebarMobile(false));
    }, [dispatch]);

    const handleCommitRename = useCallback(
        async (id: string, raw: string) => {
            setRenamingId(null);
            const trimmed = raw.trim().slice(0, 120);
            if (!trimmed) return;
            const previous = itemsRef.current.find((i) => i.conversationId === id)?.title ?? null;
            if (trimmed === (previous ?? '')) return;
            dispatch(renameLocally({ conversationId: id, title: trimmed }));
            try {
                await ChatAIHistoryService.renameConversation(id, trimmed);
            } catch (e) {
                // 404 = тред soft-deleted в другом табе/retention'ом между моментом open
                // rename и Enter. SignalR-refetch скоро уберёт его из items; ошибку юзеру
                // показывать смысла нет — он переименовывал то, чего уже нет.
                if (axios.isAxiosError(e) && e.response?.status === 404) return;
                if (previous !== null) dispatch(renameLocally({ conversationId: id, title: previous }));
                sendNotification({
                    id: `chat-ai-rename-${id}`,
                    title: 'Не удалось переименовать чат',
                    message: (e as Error).message,
                    variant: 'red',
                });
            }
        },
        [dispatch],
    );

    const performDelete = useCallback(
        async (conversationId: string) => {
            const snapshotItems = itemsRef.current;
            const snapshotTotal = totalRef.current;
            dispatch(removeLocally(conversationId));
            try {
                await ChatAIHistoryService.deleteConversation(conversationId);
            } catch (e) {
                // 404 = тред уже soft-deleted (двойной клик, race с другим табом, retention).
                // Optimistic remove корректен — ничего не откатываем и не шумим юзеру.
                if (axios.isAxiosError(e) && e.response?.status === 404) return;
                // Откат: возвращаем элементы и сохранённый total (не подменяем total длиной snapshot — M1).
                dispatch(listLoadSuccess({
                    items: snapshotItems,
                    total: snapshotTotal,
                    reset: true,
                }));
                sendNotification({
                    id: `chat-ai-delete-${conversationId}`,
                    title: 'Не удалось удалить чат',
                    message: (e as Error).message,
                    variant: 'red',
                });
            }
        },
        [dispatch],
    );

    const handleLoadMore = useCallback(() => loadListRef.current(false), []);

    const handleStartRename = useCallback((id: string) => setRenamingId(id), []);
    const handleCancelRename = useCallback(() => setRenamingId(null), []);

    return (
        <aside className={clsx('chat-ai-sidebar', { 'chat-ai-sidebar--mobile-open': sidebarOpenMobile })}>
            <div className="chat-ai-sidebar__header">
                <NewChatButton onClick={handleNewChat} />
                <button
                    type="button"
                    className="chat-ai-sidebar__mobile-close"
                    onClick={() => dispatch(toggleSidebarMobile(false))}
                    aria-label="Закрыть"
                >
                    <MdClose size={20} aria-hidden />
                </button>
            </div>
            <ConversationList
                onSelect={handleSelect}
                onStartRename={handleStartRename}
                onCommitRename={handleCommitRename}
                onCancelRename={handleCancelRename}
                onDelete={performDelete}
                onLoadMore={handleLoadMore}
                renamingId={renamingId}
            />
            <RetentionNotice />
        </aside>
    );
});
